<template>
  <!-- 高亮块组件容器 -->
  <div
    class="id_13"
    :class="{ 'embedded': embedded }"
  >
    <!-- 统一的插入功能组件 -->
    <ContentInsertion
      ref="contentInsertion"
      :features="insertFeatures"
      @insert-image="handleImageInsert"
      @insert-columns="handleColumnsInsert"
      @insert-table="handleTableInsert"
      @insert-file="handleFileInsert"
    />

    <!-- 高亮块主体，包含背景色、边框色和内容区域 -->
    <div
      class="highlight-box"
      :style="{
        background: data.bgColor || '#FFF3CD',
        borderColor: data.borderColor || '#FFC107',
        minHeight: contentHeight + 'px'
      }"
    >
      <!-- 内容区域（统一使用单一容器，避免 v-if/v-else 切换导致子组件 DOM 被销毁） -->
      <div
        class="highlight-content"
        ref="contentRef"
      ></div>
    </div>
  </div>
</template>

<script>
/**
 * 高亮块组件 - id_13
 *
 * 功能：
 * 1. 高亮内容展示与恢复（纯文本 / 混合子组件）
 * 2. 背景颜色和边框颜色（由 props.data 传入）
 * 3. 内容高度自适应
 * 4. 支持插入图片、分栏、表格、下载文件
 *
 * 组件模式：
 * - 默认模式（embedded=false）：独立组件
 * - 嵌入模式（embedded=true）：被插入到其他组件中
 */
import ContentInsertion from './ContentInsertion.vue'
import Id11 from './id_11.vue'
import Id12 from './id_12.vue'
import Id10 from './id_10.vue'
import Vue from 'vue'

export default {
  props: {
    /**
     * 外部传入的组件状态数据
     * @type {Object}
     * @property {string} text - 高亮块内容（支持HTML）
     * @property {string} bgColor - 背景颜色（十六进制格式）
     * @property {string} borderColor - 边框颜色（十六进制格式）
     * @property {Array} components - 子组件数据数组
     */
    data: Object,
    /**
     * 是否为嵌入模式（被插入到其他组件中）
     * @type {boolean}
     * @default false
     */
    embedded: {
      type: Boolean,
      default: false
    }
  },
  name: 'id_13',
  components: {
    ContentInsertion
  },
  data () {
    return {
      /**
       * 内容区域高度（px），用于自适应调整高亮块高度
       * @type {number}
       * @default 26（最小高度）
       */
      contentHeight: 26,
      /**
       * 可用的插入功能列表（高亮块内不再支持嵌套高亮）
       * @type {Array<string>}
       */
      insertFeatures: ['image', 'columns', 'table', 'file'],
      /**
       * 动态创建的组件实例列表
       * @type {Array}
       */
      mountedComponents: [],
      /**
       * 组件ID计数器
       * @type {number}
       */
      componentIdCounter: 0,
      /**
       * 组件类型映射表
       * @type {Object}
       */
      componentMap: {
        columns: Id11,
        table: Id12,
        file: Id10
      }
    }
  },
  watch: {
    /**
     * 监听内容变化：无子组件时同步 DOM，并更新高度
     */
    'data.text': {
      handler (val) {
        if (this.mountedComponents.length === 0 && this.$refs.contentRef) {
          this.$refs.contentRef.innerHTML = val || ''
        }
        this.$nextTick(() => {
          this.updateContentHeight()
        })
      }
    },
    /**
     * 监听 data.components 变化，重新初始化子组件
     */
    'data.components': {
      immediate: false,
      handler (newVal) {
        if (newVal && Array.isArray(newVal) && newVal.length > 0) {
          this.$nextTick(() => {
            this.initComponents(newVal)
          })
        }
      }
    }
  },
  methods: {
    /**
     * 获取组件完整数据（包括自身数据和插入的子组件数据）
     * @returns {Object} 完整的组件数据
     */
    getFullData () {
      const result = {
        id: 13,
        text: '',
        bgColor: (this.data && this.data.bgColor) || '',
        borderColor: (this.data && this.data.borderColor) || '',
        components: []
      }

      const insertedComponents = this.getInsertedComponentsData()
      const domToInstanceMap = {}
      insertedComponents.forEach(item => {
        if (item.dom) {
          domToInstanceMap[item.dom] = item
        }
      })

      const hasComponents = insertedComponents.length > 0

      if (this.$refs.contentRef) {
        const children = this.$refs.contentRef.children
        for (let i = 0; i < children.length; i++) {
          const child = children[i]

          if (child.classList && child.classList.contains('empty-text-paragraph') && !child.textContent) {
            continue
          }

          const componentId = this.getChildComponentId(child)

          if (componentId && componentId >= 10 && componentId <= 13) {
            const componentItem = domToInstanceMap[child]
            if (componentItem && componentItem.componentInstance) {
              const componentInstance = componentItem.componentInstance

              if (typeof componentInstance.saveState === 'function') {
                componentInstance.saveState()
              }

              const componentData = this.buildComponentData(componentId, componentInstance.data, child)
              if (componentData) {
                result.components.push({
                  type: 'component',
                  ...componentData
                })
              }
            }
          } else {
            const textContent = child.innerHTML
            if (textContent && textContent.trim() !== '' && textContent !== '<br>') {
              result.components.push({
                type: 'text',
                content: textContent
              })
            }
          }
        }
      }

      // 当存在子组件时，text 设为 null（文本已在 components 数组中）
      // 当无子组件时，text 设为 innerHTML（纯文本场景）
      result.text = hasComponents ? null : (this.$refs.contentRef ? this.$refs.contentRef.innerHTML : '')

      return result
    },

    /**
     * 从子节点 class 中解析组件数字 ID（如 id_11 -> 11）
     * @param {HTMLElement} child
     * @returns {number|null}
     */
    getChildComponentId (child) {
      const className = (child.className || '').toString()
      if (!className) return null
      const tokens = className.split(/\s+/)
      for (let j = 0; j < tokens.length; j++) {
        const token = tokens[j]
        if (token.indexOf('id_') === 0) {
          const id = parseInt(token.replace('id_', ''), 10)
          return isNaN(id) ? null : id
        }
      }
      return null
    },

    /**
     * 构建子组件的返回数据
     * @param {Number} id - 组件ID
     * @param {Object} propsData - 组件的props数据
     * @param {HTMLElement} dom - 组件的DOM元素
     * @returns {Object} 组件数据
     */
    buildComponentData (id, propsData, dom) {
      propsData = propsData || {}
      switch (id) {
        case 13:
          return {
            id: 13,
            text: propsData.text || (dom ? (dom.querySelector('.highlight-content') || {}).innerHTML || '' : ''),
            bgColor: propsData.bgColor || '',
            borderColor: propsData.borderColor || ''
          }
        case 11:
          return {
            id: 11,
            columns: propsData.columns || []
          }
        case 12:
          return {
            id: 12,
            table: propsData.table || [],
            rowBgColors: propsData.rowBgColors || {},
            colBgColors: propsData.colBgColors || {},
            cellBgColors: propsData.cellBgColors || {},
            isFullWidth: propsData.isFullWidth || false,
            hasHeaderRow: propsData.hasHeaderRow || false,
            hasHeaderCol: propsData.hasHeaderCol || false,
            colWidths: propsData.colWidths || [],
            rowHeights: propsData.rowHeights || [],
            mergedCells: propsData.mergedCells || []
          }
        case 10: {
          let listData = propsData.list
          if (typeof listData === 'string') {
            try {
              listData = JSON.parse(listData) || []
            } catch (e) {
              listData = []
            }
          }
          return {
            id: 10,
            list: listData || []
          }
        }
        default:
          return null
      }
    },

    /**
     * 打开插入功能面板
     */
    openInsertPanel () {
      this.$refs.contentInsertion.openPanel()
    },

    /**
     * 处理图片插入事件（由ContentInsertion组件触发）
     */
    handleImageInsert () {
      this.upImg()
    },

    /**
     * 处理分栏组件插入事件
     */
    handleColumnsInsert (defaultData) {
      this.insertVueComponent('columns', defaultData)
    },

    /**
     * 处理表格组件插入事件
     */
    handleTableInsert (defaultData) {
      this.insertVueComponent('table', defaultData)
    },

    /**
     * 处理文件组件插入事件
     */
    handleFileInsert (defaultData) {
      this.insertVueComponent('file', defaultData)
    },

    /**
     * 创建并挂载动态 Vue 组件，返回根 DOM（失败返回 null）
     * @param {String} type - 组件类型标识
     * @param {Object} data - 组件 props 数据
     * @returns {HTMLElement|null}
     */
    createComponentEl (type, data) {
      const Component = this.componentMap[type]
      if (!Component) {
        console.error(`未支持的组件类型: ${type}`)
        return null
      }

      this.componentIdCounter++

      const ComponentConstructor = Vue.extend(Component)
      const componentInstance = new ComponentConstructor({
        propsData: {
          data: data
        }
      })

      const tempContainer = document.createElement('div')
      tempContainer.setAttribute('contenteditable', 'false')
      componentInstance.$mount(tempContainer)

      const componentEl = componentInstance.$el
      if (!componentEl) {
        console.error('无法插入组件：组件实例创建失败')
        componentInstance.$destroy()
        return null
      }

      // 防止浏览器 contenteditable 对组件内部 DOM 进行错误重组
      componentEl.setAttribute('contenteditable', 'false')

      this.mountedComponents.push({
        id: this.componentIdCounter,
        instance: componentInstance
      })

      return componentEl
    },

    /**
     * 动态创建 Vue 组件实例并插入到内容区域
     * @param {String} type - 组件类型标识
     * @param {Object} data - 组件 props 数据
     */
    insertVueComponent (type, data) {
      const targetEl = this.$refs.contentRef
      if (!targetEl) {
        console.error('未找到目标元素')
        return
      }

      const componentEl = this.createComponentEl(type, data)
      if (!componentEl) return

      targetEl.appendChild(componentEl)

      const emptyParagraph = document.createElement('p')
      emptyParagraph.className = 'empty-text-paragraph'
      emptyParagraph.innerHTML = '<br>'
      targetEl.insertBefore(emptyParagraph, componentEl.nextSibling)

      this.$nextTick(() => {
        this.updateContentHeight()
      })
    },

    /**
     * 获取已插入的组件数据
     * @returns {Array} 组件实例信息数组
     */
    getInsertedComponentsData () {
      const result = []
      this.mountedComponents.forEach(item => {
        if (item.instance && item.instance.$el) {
          result.push({
            instanceId: item.id,
            dom: item.instance.$el,
            componentInstance: item.instance
          })
        }
      })
      return result
    },

    /**
     * 销毁所有动态创建的组件
     */
    destroyComponents () {
      this.mountedComponents.forEach(item => {
        if (item.instance) {
          const componentEl = item.instance.$el

          if (componentEl && componentEl.nextSibling &&
              componentEl.nextSibling.classList &&
              componentEl.nextSibling.classList.contains('empty-text-paragraph')) {
            componentEl.nextSibling.remove()
          }

          item.instance.$destroy()

          if (componentEl && componentEl.parentNode) {
            componentEl.parentNode.removeChild(componentEl)
          }
        }
      })
      this.mountedComponents = []
      this.componentIdCounter = 0
    },

    /**
     * 从 props.data 初始化内容
     * @description 仅在 mounted 时调用一次，避免覆盖用户编辑。
     */
    initContentFromData () {
      if (!this.data || !this.$refs.contentRef) return

      if (this.data.components && this.data.components.length > 0) {
        this.initComponents(this.data.components)
        return
      }

      if (this.data.text !== null && this.data.text !== undefined && this.data.text !== '') {
        this.$refs.contentRef.innerHTML = this.data.text
      }
    },

    /**
     * 从数据中恢复子组件和文本内容
     * @description 两阶段：先构建 DOM（文本直接渲染、组件用占位符），再替换为 Vue 组件
     * @param {Array} components - 混合内容数组（包含文本和组件）
     */
    initComponents (components) {
      if (!components || !Array.isArray(components) || components.length === 0) {
        return
      }

      const textEl = this.$refs.contentRef
      if (!textEl) return

      this.destroyComponents()

      const typeMap = {
        11: 'columns',
        12: 'table',
        10: 'file'
      }

      textEl.innerHTML = ''

      const componentPlaceholders = []
      components.forEach((item, index) => {
        if (item.type === 'text') {
          const p = document.createElement('p')
          p.innerHTML = item.content || ''
          textEl.appendChild(p)
        } else if (item.type === 'html') {
          textEl.insertAdjacentHTML('beforeend', item.content || '')
        } else if (item.type === 'component') {
          const placeholder = document.createElement('div')
          placeholder.setAttribute('data-component-index', index)
          textEl.appendChild(placeholder)
          componentPlaceholders.push({
            placeholder,
            compData: item
          })
        }
      })

      this.$nextTick(() => {
        componentPlaceholders.forEach(({ placeholder, compData }) => {
          const type = typeMap[compData.id]
          if (type) {
            this.insertVueComponentAt(type, compData, placeholder)
          }
        })

        this.$nextTick(() => {
          this.updateContentHeight()
        })
      })
    },

    /**
     * 动态创建 Vue 组件实例并替换指定占位符
     * @param {String} type - 组件类型标识
     * @param {Object} data - 组件 props 数据
     * @param {HTMLElement} placeholder - 要替换的占位符元素
     */
    insertVueComponentAt (type, data, placeholder) {
      if (!placeholder || !placeholder.parentNode) {
        console.error('无效的占位符元素')
        return
      }

      const componentEl = this.createComponentEl(type, data)
      if (!componentEl) return

      placeholder.parentNode.replaceChild(componentEl, placeholder)
    },

    /**
     * 上传图片并插入到内容区域
     */
    upImg () {
      this.$api.upImg().then((url) => {
        const str = `<img src="${url}"><div class="img_title">(图片介绍，不需要可自行删除)</div>`
        if (this.mountedComponents.length > 0) {
          const lastComponent = this.mountedComponents[this.mountedComponents.length - 1]
          if (lastComponent && lastComponent.instance && lastComponent.instance.$el) {
            const imgDiv = document.createElement('div')
            imgDiv.innerHTML = str
            const refEl = lastComponent.instance.$el
            const nextSibling = refEl.nextSibling
            if (nextSibling && nextSibling.classList && nextSibling.classList.contains('empty-text-paragraph')) {
              this.$refs.contentRef.insertBefore(imgDiv, nextSibling)
            } else {
              this.$refs.contentRef.appendChild(imgDiv)
            }
          }
        } else {
          this.$refs.contentRef.innerHTML = this.$refs.contentRef.innerHTML + str
        }
        this.$nextTick(() => {
          this.updateContentHeight()
        })
      })
    },

    /**
     * 更新内容区域高度
     * @description 根据内容实际高度动态调整高亮块最小高度
     */
    updateContentHeight () {
      if (this.$refs.contentRef) {
        const rect = this.$refs.contentRef.getBoundingClientRect()
        this.contentHeight = Math.max(26, rect.height)
      }
    }
  },
  mounted () {
    this.$nextTick(() => {
      this.initContentFromData()
    })
  },
  beforeDestroy () {
    this.destroyComponents()
  }
}
</script>

<style lang="scss" scoped>
.id_13 {
  padding-top: 40px;
  background-color: transparent;
  position: relative;

  .highlight-box {
    padding: 10px 16px;
    border: 2px solid;
    border-radius: 8px;
    transition: all 0.3s ease;
    position: relative;
    cursor: text;
  }

  .highlight-content {
    font-size: 14px;
    line-height: 1.6;
    color: #303133;
    outline: none;
    min-height: 22px;
    word-break: break-word;
    white-space: pre-wrap;

    /deep/ .id_11, /deep/ .id_12, /deep/ .id_13 {
      padding: 0;
      padding-top: 12px;
    }

    > div[contenteditable="false"] {
      margin-top: 16px;
      margin-bottom: 16px;
      outline: none;
    }

    > p.empty-text-paragraph {
      margin: 0;
      padding: 0;
      min-height: 1.6em;
      line-height: 1.6;
      font-size: 14px;
      color: #333;
      outline: none;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/module/course/public/id_13.vue